import Axios from 'axios'
import tokenStore from '../Utils/tokenStore'

const IDENTITY_BASE_URL =
  process.env.REACT_APP_IDENTITY_API_BASE_URL || 'https://identity.vizybility.com';
const IDENTITY_API_VERSION =
  process.env.REACT_APP_IDENTITY_API_VERSION || 'v1';

// Dedicated axios instance for token refresh — bypasses the global interceptors
const refreshAxios = Axios.create()

// ─── Request interceptor — attach bearer token ────────────────────────────────

Axios.interceptors.request.use(async config => {
  const token = tokenStore.getRawToken()
  config.headers = {
    'Authorization': 'Bearer ' + token,
    'accept': 'application/json',
    'Content-Type': 'application/json',
    ...config.headers,
  }
  config.timeout = 30000
  return config
})

// ─── Response interceptor — 401 → refresh → retry ────────────────────────────

let _isRefreshing = false
let _refreshQueue = []

const processQueue = (error, token = null) => {
  _refreshQueue.forEach(p => error ? p.reject(error) : p.resolve(token))
  _refreshQueue = []
}

Axios.interceptors.response.use(
  response => response,
  async error => {
    const original = error?.config
    const refreshUrl = `${IDENTITY_BASE_URL}/api/${IDENTITY_API_VERSION}/auth/token/refresh`
    const isRefreshCall = original?.url?.includes('/auth/token/refresh')

    if (error?.response?.status === 401 && !original?._retry && !isRefreshCall) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject })
        }).then(token => {
          original.headers['Authorization'] = `Bearer ${token}`
          return Axios(original)
        })
      }

      original._retry = true
      const slot = tokenStore.getStorageSlot()
      const rememberMe = slot?.rm !== false
      const refreshToken = slot?.r

      if (!rememberMe || !refreshToken) {
        tokenStore.removeToken()
        if (window.location.pathname !== '/') window.location.href = '/'
        return Promise.reject(error)
      }

      _isRefreshing = true

      try {
        const resp = await refreshAxios.post(
          refreshUrl,
          { token: slot?.t, refreshToken },
          { headers: { 'Accept-Language': 'en', 'Content-Type': 'application/json' } },
        )
        const data = resp?.data?.data ?? resp?.data
        const newToken = data?.token || data?.Token
        const newRefreshToken = data?.refreshToken || data?.RefreshToken

        if (!newToken) throw new Error('Refresh returned no token.')

        tokenStore.saveToken({
          token: newToken,
          refreshToken: newRefreshToken || refreshToken,
          rememberMe: slot?.rm !== false,
          profile: slot?.p || null,
          metadata: slot?.m || null,
        })

        processQueue(null, newToken)
        original.headers['Authorization'] = `Bearer ${newToken}`
        return Axios(original)
      } catch (refreshError) {
        processQueue(refreshError)
        tokenStore.removeToken()
        if (window.location.pathname !== '/') window.location.href = '/'
        return Promise.reject(refreshError)
      } finally {
        _isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

// ─── API helpers ──────────────────────────────────────────────────────────────

const getRequestApi = url => {
  return new Promise((resolve, reject) => {
    Axios.get(url)
      .then(result => {
        resolve({
          errorStatus: false,
          statusCode: result.status,
          data: result.data,
        })
      })
      .catch(error => {
        if (error?.response) {
          resolve({
            errorStatus: true,
            statusCode: error?.response.status,
            data: error?.response.data,
          })
        } else {
          reject(error)
        }
      })
  })
}

const postRequestApi = (url, data, headers) => {
  return new Promise((resolve, reject) => {
    Axios.post(url, data, { headers })
      .then(result => {
        resolve({
          errorStatus: false,
          statusCode: result.status,
          data: result.data,
          length: result?.headers?.['x-pagination']
            ? JSON.parse(result?.headers?.['x-pagination'])
            : null,
        })
      })
      .catch(error => {
        if (error?.response) {
          resolve({
            errorStatus: true,
            statusCode: error?.response.status,
            data: error?.response.data,
          })
        } else {
          reject(error)
        }
      })
  })
}

const patchRequestApi = (url, data, headers) => {
  return new Promise((resolve, reject) => {
    Axios.patch(url, data, { headers })
      .then(result => {
        resolve({
          errorStatus: false,
          statusCode: result.status,
          data: result.data,
        })
      })
      .catch(error => {
        if (error?.response) {
          resolve({
            errorStatus: true,
            statusCode: error?.response.status,
            data: error?.response.data,
          })
        } else {
          reject(error)
        }
      })
  })
}

const putRequestApi = (url, data, headers) => {
  return new Promise((resolve, reject) => {
    Axios.put(url, data, { headers })
      .then(result => {
        resolve({
          errorStatus: false,
          statusCode: result.status,
          data: result.data,
        })
      })
      .catch(error => {
        if (error?.response) {
          resolve({
            errorStatus: true,
            statusCode: error?.response.status,
            data: error?.response.data,
          })
        } else {
          reject(error)
        }
      })
  })
}

const deleteRequestApi = (url, data) => {
  return new Promise((resolve, reject) => {
    Axios.delete(url, { data })
      .then(result => {
        resolve({
          errorStatus: false,
          statusCode: result.status,
          data: result.data,
        })
      })
      .catch(error => {
        if (error?.response) {
          resolve({
            errorStatus: true,
            statusCode: error?.response.status,
            data: error?.response.data,
          })
        } else {
          reject(error)
        }
      })
  })
}

export const Api = {
  getRequestApi,
  postRequestApi,
  patchRequestApi,
  putRequestApi,
  deleteRequestApi,
}
